import { Box, Typography, Button, useTheme } from '@mui/material';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as api from '../api';
import filledCreditCardIcon from '../assets/icons/credit-card-filled.svg';
import paymentStepperIcon from '../assets/icons/credit-card.svg';
import {
  CreditCardForm,
  CreditCardFormProps,
} from '../components/CreditCardForm';
import { Stepper } from '../components/Stepper';

import { BalanceList } from './BalanceList';

import SvgIcon from '../components/SvgIcon';
import {
  formatAmountInCurrency,
  withSelectedOrder,
  withTotalPayment,
} from '../utils';
import { CreditCardContext } from '../context/CreditCardContext';
import { BalanceContext } from '../context/BalanceContext';

export function PaymentPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const nav = useNavigate();
  const { setCreditCard } = useContext(CreditCardContext);
  const { ccValues, setCcValues } = useContext(CreditCardContext);
  const { instalment, setInstalment } = useContext(CreditCardContext);
  const { balances, balancesSelection } = useContext(BalanceContext);
  const selectedBalances = withSelectedOrder(
    balances || [],
    balancesSelection || [],
  );
  const totalPayment = withTotalPayment(selectedBalances);

  const [ccError, setCCError] = React.useState<string | null>(null);

  const handleCreditCardChange: CreditCardFormProps['onChange'] = async (
    field,
    value,
  ) => {
    setCcValues({ ...ccValues, [field]: value });

    if (field === 'ccNo') {
      if (value.length >= 6) {
        try {
          const res = await api.creditCard({
            ccNumber: value,
            amount: Math.ceil(totalPayment.amount * 100) / 100,
            currencyCode: totalPayment.currency.code,
          });
          setCCError(null);
          setCreditCard(res);
          if (res.installments.length > 1) {
            setInstalment();
          } else {
            setInstalment(res.installments[0]);
          }
        } catch (err: any) {
          setCCError(err.message);
          setCreditCard();
          setInstalment();
        }
      } else {
        setCreditCard();
        setInstalment();
      }
    }
  };

  const handlePayment = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!instalment && totalPayment.amount > 0) {
      return;
    }

    nav('/payment-gw', { replace: true });
  };

  return (
    <Box
      m={[2, 3, 3, 4]}
      sx={{
        border: ['none', '2px solid #eee'],
        borderRadius: '10px',
        p: ['8px', '12px', '24px', '24px'],
      }}
    >
      <Stepper
        step={2}
        total={2}
        label={t('paymentPage.title')}
        labelIcon={paymentStepperIcon}
      />

      {selectedBalances.length > 0 && (
        <Box
          sx={{
            alignItems: 'center',
            justifyContent: 'end',
            display: ['flex', 'flex', 'flex', 'none'],
            gap: 2,
          }}
        >
          {totalPayment.loyaltyAmount > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: '90px',
              }}
            >
              <Typography color="primary">
                {formatAmountInCurrency(
                  Math.ceil(totalPayment.loyaltyAmount * 100) / 100,
                  totalPayment.currency.code,
                )}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '9px' }}>
                {t('payment.totalLoyalityAmount')}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: '90px',
            }}
          >
            <Typography color="#BE9A02">
              {formatAmountInCurrency(
                Math.ceil(totalPayment.amount * 100) / 100,
                totalPayment.currency.code,
              )}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '9px' }}>
              {t('payment.totalAmount')}
            </Typography>
          </Box>
        </Box>
      )}

      <BalanceList
        balances={selectedBalances}
        balancesSelection={balancesSelection || []}
        readonly
      />
      <Box
        sx={{
          my: 4,
          borderBottom: '2px solid #eee',
          display: ['none', 'block'],
          flex: 1,
          height: 1,
        }}
      />
      <form style={{ width: '100%' }} onSubmit={handlePayment}>
        {totalPayment.amount > 0 && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }} mb={2}>
              <SvgIcon
                src={filledCreditCardIcon}
                style={{
                  fill: theme.palette.secondary.main,
                  width: 24,
                }}
              />
              <Typography sx={{ ml: 2 }}>
                {t('payment.payWithCreditCard')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 4, pl: 4 }}>
              <CreditCardForm
                values={ccValues}
                onChange={handleCreditCardChange}
                errors={{
                  ccNo: ccError,
                }}
              />
            </Box>
          </Box>
        )}

        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: 4,
            justifyContent: 'end',
          }}
        >
          <Button
            variant="contained"
            type="button"
            sx={{ mr: 2 }}
            onClick={() => nav(-1)}
          >
            {t('payment.backBtn')}
          </Button>
          <Button
            type="submit"
            color="secondary"
            variant={ccError ? 'outlined' : 'contained'}
            disabled={Boolean(ccError)}
          >
            {t('payment.continueBtn')}
          </Button>
        </Typography>
      </form>
    </Box>
  );
}
